import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  LayoutGrid, Megaphone, PlusCircle, Wallet, PhoneCall, ShieldCheck, Bell, LifeBuoy, FolderOpen, User, LogOut, Menu, X,
} from "lucide-react";
import { PUBLISHER } from "./demoData";
import Overview from "./views/Overview";
import MyOffers from "./views/MyOffers";
import ApplyOffers from "./views/ApplyOffers";
import Payouts from "./views/Payouts";
import CallLog from "./views/CallLog";
import Quality from "./views/Quality";
import Announcements from "./views/Announcements";
import Support from "./views/Support";
import Documents from "./views/Documents";
import Profile from "./views/Profile";

const NAV = [
  ["overview", "Dashboard", LayoutGrid, Overview],
  ["offers", "My Offers", Megaphone, MyOffers],
  ["apply", "Apply for Offers", PlusCircle, ApplyOffers],
  ["payouts", "Payouts & Invoices", Wallet, Payouts],
  ["calls", "Call Log", PhoneCall, CallLog],
  ["quality", "Quality Score", ShieldCheck, Quality],
  ["news", "Announcements", Bell, Announcements],
  ["support", "Support", LifeBuoy, Support],
  ["docs", "Documents", FolderOpen, Documents],
  ["profile", "Profile", User, Profile],
];

const Sidebar = ({ tab, setTab, onLogout, open, close }) => (
  <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-64 flex-shrink-0 bg-[#0b0f15] border-r border-slate-800 flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="demo-sidebar">
    <div className="flex items-center justify-between px-5 py-5 border-b border-slate-800">
      <div>
        <p className="font-semibold text-white">Digital Master Media</p>
        <p className="text-[10px] tracking-[0.2em] text-slate-500">PUBLISHER PANEL</p>
      </div>
      <button className="lg:hidden text-slate-400" onClick={close} data-testid="demo-sidebar-close"><X className="w-5 h-5" /></button>
    </div>
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
      {NAV.map(([key, label, Icon]) => (
        <button key={key} onClick={() => { setTab(key); close(); }} data-testid={`demo-nav-${key}`}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === key ? "bg-blue-500/15 text-blue-300" : "text-slate-300 hover:bg-slate-800/60"}`}>
          <Icon className="w-4 h-4" /> {label}
        </button>
      ))}
    </nav>
    <div className="p-4 border-t border-slate-800">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-sm font-bold">MC</div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate">{PUBLISHER.name}</p>
          <p className="text-[11px] text-slate-500 truncate">{PUBLISHER.id} · {PUBLISHER.tier}</p>
        </div>
        <button onClick={onLogout} className="text-slate-400 hover:text-red-400" data-testid="demo-logout"><LogOut className="w-4 h-4" /></button>
      </div>
    </div>
  </aside>
);

export default function DemoPanel() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  if (sessionStorage.getItem("dmm_publisher_demo") !== "1") return <Navigate to="/publisher-demo" replace />;

  const View = NAV.find((n) => n[0] === tab)[3];
  const logout = () => { sessionStorage.removeItem("dmm_publisher_demo"); nav("/publisher-demo"); };

  return (
    <div className="min-h-screen bg-[#070a0f] text-slate-100 font-['Inter',sans-serif] flex" data-testid="demo-panel">
      <Toaster theme="dark" position="top-right" />
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <Sidebar tab={tab} setTab={setTab} onLogout={logout} open={open} close={() => setOpen(false)} />
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 sm:px-8 py-3.5 bg-[#070a0f]/85 backdrop-blur-xl border-b border-slate-800">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="demo-menu-open"><Menu className="w-5 h-5" /></button>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1" data-testid="demo-badge">Demo · Sample Data</span>
          <button onClick={() => setTab("news")} className="relative text-slate-300 hover:text-white" data-testid="demo-bell">
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-500" />
          </button>
        </header>
        <main className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1600px] mx-auto" data-testid={`demo-view-${tab}`}>
          <View goTo={setTab} />
        </main>
      </div>
    </div>
  );
}
